import { useMemo } from 'react';
import { Accordion, Badge } from 'react-bootstrap';

import {
  buildSyntheticDeveloperRecommendationsFromAggregate,
  parseDeveloperRecommendationsList
} from './biasSummaryReference.utils';
import BiasLlmPromptEntriesAccordion from './BiasLlmPromptEntriesAccordion';
import './BiasDetectionChartsSection.css';
import './BiasDeveloperRemediationSection.css';

export type BiasDeveloperRecommendation = {
  priority?: number;
  display_name?: string;
  subtitle?: string;
  summary?: string;
  status?: string;
  value?: number;
  fix_pill_label?: string | null;
  what_this_means?: string;
  entries_note?: string;
  recommended_fix_types?: string[];
  suggested_fixes?: string[];
  /** Optional caption for PASS rows, e.g. "threshold warn 0.2 / fail 0.3" */
  threshold_caption?: string;
  warn_threshold?: number;
  fail_threshold?: number;
};

const FIX_COPY: Record<'output' | 'prompt' | 'metadata', string> = {
  output: 'Fix: Add output guardrail or tighten system prompt constraints',
  prompt: 'Fix: Add input validation or pre-processing filter',
  metadata: 'No action needed'
};

const CARD_DEF: {
  key: 'output' | 'prompt' | 'metadata';
  title: string;
}[] = [
  { key: 'output', title: 'Output (LLM response)' },
  { key: 'prompt', title: 'Prompt (user input)' },
  { key: 'metadata', title: 'Metadata' }
];

function normalizeStatus(status: string | undefined): 'fail' | 'watch' | 'pass' | 'neutral' {
  const s = (status || '').toLowerCase();
  if (s === 'fail' || s === 'failed' || s === 'reject') return 'fail';
  if (s === 'watch' || s === 'review' || s === 'warn') return 'watch';
  if (s === 'pass' || s === 'passed' || s === 'approve') return 'pass';
  return 'neutral';
}

function countFromSourceMap(map: unknown): number {
  if (!map || typeof map !== 'object') return 0;
  let n = 0;
  for (const v of Object.values(map as Record<string, unknown>)) {
    const num = Number(v);
    if (!Number.isNaN(num) && typeof v !== 'boolean') n += num;
  }
  return n;
}

function termsWithCounts(map: unknown): { term: string; count: number }[] {
  if (!map || typeof map !== 'object') return [];
  return Object.entries(map as Record<string, unknown>).map(([term, v]) => {
    const num = Number(v);
    const count = Number.isFinite(num) && !Number.isNaN(num) ? num : 1;
    return { term, count: Math.max(1, count) };
  });
}

function readCountForLayer(
  aggregateSummary: Record<string, unknown> | undefined,
  key: 'output' | 'prompt' | 'metadata',
  termsBySource: Record<string, unknown>
): number {
  const se = aggregateSummary?.vulnerability_surface as Record<string, unknown> | undefined;
  const exposure = se?.sensitive_exposure as Record<string, unknown> | undefined;
  if (exposure && typeof exposure === 'object') {
    const raw = exposure[key];
    if (raw !== undefined && raw !== null && raw !== '') {
      const n = Number(raw);
      if (Number.isFinite(n)) return n;
    }
  }
  return countFromSourceMap(termsBySource[key]);
}

function thresholdCaption(rec: BiasDeveloperRecommendation): string | null {
  if (rec.threshold_caption && String(rec.threshold_caption).trim()) return String(rec.threshold_caption).trim();
  const w = rec.warn_threshold;
  const f = rec.fail_threshold;
  if (typeof w === 'number' && typeof f === 'number' && Number.isFinite(w) && Number.isFinite(f)) {
    return `threshold warn ${w} / fail ${f}`;
  }
  return null;
}

const WrenchIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden focusable="false">
    <path
      fill="currentColor"
      d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"
    />
  </svg>
);

type Props = {
  aggregateSummary: Record<string, unknown> | null | undefined;
  entries?: Record<string, unknown>[] | null;
};

const BiasDeveloperRemediationSection = ({ aggregateSummary, entries }: Props) => {
  const recommendations = useMemo((): BiasDeveloperRecommendation[] => {
    const parsed = parseDeveloperRecommendationsList(aggregateSummary?.developer_recommendations);
    if (parsed.length > 0) {
      return parsed as BiasDeveloperRecommendation[];
    }
    return buildSyntheticDeveloperRecommendationsFromAggregate(
      aggregateSummary ?? undefined
    ) as BiasDeveloperRecommendation[];
  }, [aggregateSummary]);

  const termsBySource = (aggregateSummary?.non_whitelisted_sensitive_by_source ??
    aggregateSummary?.sensitive_by_source ??
    {}) as Record<string, unknown>;

  const hasTermsLayer =
    termsBySource &&
    typeof termsBySource === 'object' &&
    (['output', 'prompt', 'metadata'] as const).some(
      k =>
        termsBySource[k] && typeof termsBySource[k] === 'object' && Object.keys(termsBySource[k] as object).length > 0
    );

  const hasVuln =
    aggregateSummary &&
    aggregateSummary.vulnerability_surface != null &&
    typeof aggregateSummary.vulnerability_surface === 'object';

  const showSensitive = Boolean(hasVuln || hasTermsLayer);
  const showQueue = recommendations.length > 0;
  const entryList = Array.isArray(entries) ? entries : [];
  const showFullReview = entryList.length > 0;

  if (!showSensitive && !showQueue && !showFullReview) return null;

  const vd = (aggregateSummary?.verdict_distribution ?? {}) as Record<string, unknown>;
  const rejectN = Number(vd.REJECT ?? 0) || 0;
  const approveN = Number(vd.APPROVE ?? 0) || 0;
  const reviewN = Number(vd.REVIEW ?? 0) || 0;
  const totalFromAgg = aggregateSummary?.total_entries;
  const entriesCount = entryList.length > 0 ? entryList.length : typeof totalFromAgg === 'number' ? totalFromAgg : 0;

  return (
    <div className="bias-dev-remediation mb-2">
      {showSensitive ? (
        <div className="mb-2">
          <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-1 mb-2">
            <span className="bias-dev-section-title">Where are sensitive terms appearing?</span>
          </div>
          <div className="bias-dev-sensitive-row">
            {CARD_DEF.map(({ key, title }) => {
              const count = readCountForLayer(aggregateSummary ?? undefined, key, termsBySource);
              const hasIssue = count > 0;
              const accent = key === 'output' && hasIssue ? 'red' : hasIssue ? 'amber' : 'green';
              const cardClass =
                accent === 'red'
                  ? 'bias-dev-sensitive-card bias-dev-sensitive-card--red'
                  : accent === 'amber'
                  ? 'bias-dev-sensitive-card bias-dev-sensitive-card--amber'
                  : 'bias-dev-sensitive-card bias-dev-sensitive-card--green';
              const countClass =
                accent === 'red'
                  ? 'bias-dev-sensitive-card__count bias-dev-sensitive-card__count--red'
                  : accent === 'amber'
                  ? 'bias-dev-sensitive-card__count bias-dev-sensitive-card__count--amber'
                  : 'bias-dev-sensitive-card__count bias-dev-sensitive-card__count--green';
              const pills = termsWithCounts(termsBySource[key]);

              return (
                <div key={key} className={cardClass}>
                  <div className="bias-dev-sensitive-card__title">{title}</div>
                  <div className={countClass}>
                    {count} term{count === 1 ? '' : 's'}
                  </div>
                  <div className="bias-dev-sensitive-card__fix">→ {FIX_COPY[key]}</div>
                  {pills.length > 0 ? (
                    <div className="d-flex flex-wrap">
                      {pills.map(({ term, count: c }) => (
                        <span
                          key={term}
                          className={
                            accent === 'green' ? 'bias-dev-term-pill bias-dev-term-pill--neutral' : 'bias-dev-term-pill'
                          }
                        >
                          {term}
                          {c > 1 ? ` x${c}` : ''}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {showQueue ? (
        <div className="bias-dev-queue-wrap bias-dev-queue mb-2">
          <div className="bias-dev-queue-head">
            <span className="bias-dev-section-title mb-0">Issue priority queue</span>
            <span className="bias-dev-section-hint">Sorted by severity · click to expand</span>
          </div>
          <Accordion alwaysOpen flush>
            {recommendations.map((rec, idx) => {
              const st = normalizeStatus(rec.status);
              const dotColor =
                st === 'fail' ? '#d32f2f' : st === 'watch' ? '#a16207' : st === 'pass' ? '#15803d' : '#94a3b8';
              const badgeBg =
                st === 'fail' ? 'danger' : st === 'watch' ? 'warning' : st === 'pass' ? 'success' : 'secondary';
              const val =
                rec.value != null && typeof rec.value === 'number' && !Number.isNaN(rec.value)
                  ? rec.value.toFixed(3)
                  : rec.value != null
                  ? String(rec.value)
                  : '—';
              const statusLabel = (
                rec.status != null && String(rec.status).trim() ? String(rec.status) : '—'
              ).toUpperCase();
              const subtitle = rec.subtitle || rec.summary || '';
              const displayName = rec.display_name || 'Metric';
              const thresholdMeta = thresholdCaption(rec);

              return (
                <Accordion.Item eventKey={`q-${idx}`} key={`q-${idx}-${displayName}`}>
                  <Accordion.Header>
                    <div className="d-flex w-100 align-items-start gap-2 flex-wrap">
                      <div className="bias-dev-queue-row__left flex-grow-1 min-w-0 me-1">
                        <span className="bias-dev-queue-dot" style={{ background: dotColor }} />
                        <div className="min-w-0 d-inline-block" style={{ verticalAlign: 'top', maxWidth: '100%' }}>
                          <div className="bias-dev-queue-title">
                            {displayName}
                            {subtitle ? (
                              <>
                                {' '}
                                <span className="bias-dev-queue-sub">— {subtitle}</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="bias-dev-queue-badges flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <Badge
                          bg={badgeBg}
                          text={badgeBg === 'warning' ? 'dark' : undefined}
                          className="rounded-pill fw-semibold text-uppercase"
                          style={{ fontSize: '0.68rem' }}
                        >
                          {statusLabel} · {val}
                        </Badge>
                        {rec.fix_pill_label ? (
                          <span className="bias-dev-fix-pill">
                            <WrenchIcon />
                            {String(rec.fix_pill_label)}
                          </span>
                        ) : null}
                        {thresholdMeta ? <span className="bias-dev-pass-threshold">{thresholdMeta}</span> : null}
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="fw-semibold text-body-secondary small mb-1">What this means for you</div>
                    <p className="mb-2 text-body lh-sm">{rec.what_this_means || '—'}</p>
                    {rec.entries_note ? (
                      <div className="rounded-3 border border-secondary border-opacity-25 bg-white px-2 py-2 mb-2 small text-body-secondary fst-italic">
                        {rec.entries_note}
                      </div>
                    ) : null}
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                      <div className="d-flex flex-wrap align-items-center gap-2 min-w-0">
                        <span className="fw-semibold text-body-secondary small flex-shrink-0">Suggestion fix</span>
                        {Array.isArray(rec.recommended_fix_types) && rec.recommended_fix_types.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {rec.recommended_fix_types.map(t => (
                              <Badge key={t} bg="light" text="dark" className="rounded-pill border fw-normal">
                                {t}
                              </Badge>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      {Array.isArray(rec.suggested_fixes) && rec.suggested_fixes.length > 0 ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary py-0 px-2 flex-shrink-0"
                          style={{ fontSize: '0.72rem' }}
                          onClick={() => {
                            void navigator.clipboard.writeText(rec.suggested_fixes!.map(s => String(s)).join('\n\n'));
                          }}
                        >
                          Copy
                        </button>
                      ) : null}
                    </div>
                    {Array.isArray(rec.suggested_fixes) && rec.suggested_fixes.length > 0 ? (
                      <pre
                        className="mb-0 small border rounded-3 p-2 bg-white text-body"
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxHeight: 240,
                          overflow: 'auto',
                          fontSize: '0.78rem'
                        }}
                      >
                        {rec.suggested_fixes.map(s => String(s)).join('\n\n')}
                      </pre>
                    ) : (
                      <p className="small text-body-secondary mb-2">—</p>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </div>
      ) : null}

      {showFullReview ? (
        <Accordion className="bias-detection-accordion border-0 rounded-4 shadow-sm mb-2 mt-2 overflow-hidden">
          <Accordion.Item eventKey="full-review" className="border-0 bg-white">
            <Accordion.Header className="bias-detection-accordion__header px-2 py-1 fw-semibold">
              <div className="d-flex flex-wrap align-items-center justify-content-between w-100 gap-2 pe-1 text-start">
                <div className="d-flex flex-wrap align-items-center gap-2 min-w-0">
                  <span className="text-body">All prompts & outputs — full review</span>
                  <Badge bg="secondary" className="rounded-pill fw-semibold" style={{ fontSize: '0.68rem' }}>
                    {entriesCount} entries
                  </Badge>
                  <Badge bg="danger" className="rounded-pill fw-semibold" style={{ fontSize: '0.68rem' }}>
                    {rejectN} rejected
                  </Badge>
                  <Badge bg="success" className="rounded-pill fw-semibold" style={{ fontSize: '0.68rem' }}>
                    {approveN} approved
                  </Badge>
                  {reviewN > 0 ? (
                    <Badge
                      bg="warning"
                      text="dark"
                      className="rounded-pill fw-semibold"
                      style={{ fontSize: '0.68rem' }}
                    >
                      {reviewN} review
                    </Badge>
                  ) : null}
                </div>
                <span
                  className="small text-body-secondary text-start text-md-end flex-shrink-1"
                  style={{ maxWidth: '28rem' }}
                >
                  Each row shows prompt and model output → flagged terms in red · click header to collapse
                </span>
              </div>
            </Accordion.Header>
            <Accordion.Body className="p-2 bias-detection-accordion__body--llm">
              <BiasLlmPromptEntriesAccordion entries={entryList} />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      ) : null}
    </div>
  );
};

export default BiasDeveloperRemediationSection;
